import React, { useEffect, useState, useRef } from 'react';
import { connect, useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import { Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import { DownloadOutlined, LoadingOutlined } from '@ant-design/icons';
import moment from 'moment';
import { isEmpty } from 'lodash';
import { useMediaQuery } from 'react-responsive';

import { AppState } from '../../app-redux/reducers';
import { getAllLegalDocuments } from '../../app-redux/actions/legal_documents.action';
import { AppConstants } from '../../core/app.constants';
import { LegalDocument } from 'core/legalDocuments/models';
import { LocationOfResidenceDropdownComponent } from 'components/LocationOfResidenceDropdown';
import { translationWrapper } from 'utils/common-methods';
import { AvailableDocumentTypes } from 'utils/enums';
import { getRolloutLoading } from 'app-redux/selectors/rollout.selector';
import './styles.less';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

interface LegalDocumentsContentComponentProps {
  currentCountry: string;
  documentTypeSelected: string;
  redirectToCurrentCountryLegalDocsScreen: any;
  getAllLegalDocuments: (countryCode: string) => any;
}

interface ICurrentDocumentInfo {
  title: string;
  location: string;
  startDate: any;
  filepath: string;
}

const DownloadFileButton = (props: any) => (
  <div className='download-file-button'>
    <DownloadOutlined />
    <a className={isEmpty(props.filePath) ? 'button-disabled' : ''} href={props.filePath} download>{props.t('global.button.download')}</a>
  </div>
);

const LegalDocumentsContentComponent: React.FC<LegalDocumentsContentComponentProps> = (props) => {
  const isRolloutLoading = useSelector(getRolloutLoading)
  
  const { t } = translationWrapper(useTranslation());
  const [legalDocuments, setLegalDocuments] = useState<LegalDocument[]>([]);
  const [isLoadingLegalDocuments, setIsLoadingLegalDocuments] = useState(false);
  const [currentDocumentInfo, setCurrentDocumentInfo] = useState<ICurrentDocumentInfo>();
  const [numberOfPages, setNumberOfPages] = useState(1);
  const isDesktopOrLaptop = useMediaQuery({ minDeviceWidth: 1224 });
  const [pageWidth, setPageWidth] = useState<number>(0);

  const currentLocationSelected = AppConstants.SUPPORTED_COUNTRIES.find((lang: any) => lang.value === props.currentCountry)?.name || '';

  const fetchAllLegalDocumentsByCountry = async () => {
    setIsLoadingLegalDocuments(true);
    if (props.currentCountry !== ':COUNTRY') {
      const countryInformation = AppConstants.SUPPORTED_COUNTRIES.filter((country) => country.value === props.currentCountry);
      const countryCode = (countryInformation.length >= 1) ? countryInformation[0].value : '';
      setLegalDocuments(await props.getAllLegalDocuments(countryCode));
    }
    setIsLoadingLegalDocuments(false);
  }

  const onCountrySelect = (value: string) => {
    props.redirectToCurrentCountryLegalDocsScreen(value);
  };

  const onDocumentLoadSuccess = (value: any) => {
    setNumberOfPages(value.numPages);
  }

  useEffect(() => {
    if(!isRolloutLoading) fetchAllLegalDocumentsByCountry();
  }, [props.currentCountry, isRolloutLoading]);

  function removeTextLayerOffset() {
    const textLayers = window.document.querySelectorAll(".react-pdf__Page__textContent");
    textLayers.forEach((layer: any) => {
        const { style } = layer;
        style.top = "0";
        style.left = "0";
        style.transform = "";
    });
}

  useEffect(() => {
    if (legalDocuments) {
      const sortedLegalDocuments = legalDocuments?.filter((legalDoc: any) => legalDoc.startDate).sort((a: any, b: any) => moment(b.startDate).valueOf() - moment(a.startDate).valueOf());
      if (props.documentTypeSelected === AvailableDocumentTypes.TERMS) {
        setCurrentDocumentInfo({
          title: 'global.text.terms_and_conditions',
          location: currentLocationSelected,
          startDate: moment(sortedLegalDocuments?.find((legalDoc: any) => legalDoc.typeRow === 'terms_and_conditions')?.startDate)?.format('D MMM YYYY'),
          filepath: sortedLegalDocuments?.find((legalDoc: any) => legalDoc.typeRow === 'terms_and_conditions')?.filepath || ''
        });
      } else if (props.documentTypeSelected === AvailableDocumentTypes.PRIVACY) {
        setCurrentDocumentInfo({
          title: 'global.text.privacy_notice',
          location: currentLocationSelected,
          startDate: moment(sortedLegalDocuments?.find((legalDoc: any) => legalDoc.typeRow === 'privacy_notice')?.startDate)?.format('D MMM YYYY'),
          filepath: sortedLegalDocuments?.find((legalDoc: any) => legalDoc.typeRow === 'privacy_notice')?.filepath || ''
        });
      }
    }
  }, [legalDocuments, props.documentTypeSelected]);

  /**
   * foolowing code is to set page with for pdf file
   */
  const pdfContainerRef = useRef<HTMLDivElement>(document.createElement('div'));
  useEffect(() => {
    if (pdfContainerRef.current) {
      setPageWidth(pdfContainerRef.current.clientWidth);
    }
  }, [pdfContainerRef])

  const {
    title = "",
    filepath = "",
    location = "",
    startDate,
  } = currentDocumentInfo || {};

  return (
    <div className={isDesktopOrLaptop ? 'legaldocs-pdf-container padding-container' : 'legaldocs-pdf-container'}>
      { !isDesktopOrLaptop &&
        <LocationOfResidenceDropdownComponent
          t={t}
          onCountrySelect={onCountrySelect}
          currentCountry={props.currentCountry}
        />
      }
     {currentDocumentInfo && <div
        className={isEmpty(filepath) ? 'document-pdf-container pdf-min-height' : 'document-pdf-container'}
        ref={pdfContainerRef}
      >
          <div className={(isDesktopOrLaptop)? 'document-pdf-header' : 'document-pdf-header document-pdf-header-mobile'}>
               <div style={{flexDirection: 'column', width: '85%'}}>
                 <div style={{ color: '#363948', fontWeight: 'bolder' }}>{t(title)}</div>
                 <div>{`${location} • ${startDate ?? ''}`}</div>
               </div>
              <div style={{ right: '0' }}><DownloadFileButton filePath={filepath} t={t}/></div>
          </div>
        <Spin spinning={isLoadingLegalDocuments} tip={t('global.info.loading')} indicator={<LoadingOutlined />}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%'}}>
            <div className={!isDesktopOrLaptop ? 'document-pdf-selected pdf-width-for-mobile' : 'document-pdf-selected'}>
              {
                !isEmpty(filepath) ?
                  <Document
                    file={filepath}
                    onLoadSuccess={onDocumentLoadSuccess}
                    className='document-pdf-pages-display'
                  >
                    {Array.from(new Array(numberOfPages), (_el, index) => (
                      <Page
                        key={`page_${index + 1}`}
                        pageNumber={index + 1}
                        width={pageWidth}
                        onLoadSuccess={removeTextLayerOffset}
                      />
                    ))}
                  </Document>
                  :
                  <div>{t('no_pdf_file_selected')}</div>
              }
            </div>
          </div>
        </Spin>
      </div>}
    </div>
  );
};

const mapStateToProps = (state: AppState, props: any) => ({});

const mapDispatchToProps = (dispatch: any) => ({
  getAllLegalDocuments: (countryCode: string) => dispatch(getAllLegalDocuments(countryCode)),
});

export const LegalDocumentsContent = withRouter(
  connect(mapStateToProps, mapDispatchToProps)(LegalDocumentsContentComponent),
);
